import { useEffect, useState } from 'react';
import { fetchProject, type Project } from './api';
import { applyTheme } from './theme';
import { RegisterPage } from './pages/register';
import { RoomPage } from './pages/room';
import { CancelPage, NewsletterPage, ReschedulePage } from './pages/manage';
import { ReplayPage } from './pages/replay';
import { LandingPage, LegalPage, TrialPage } from './pages/landing';
import { AdminApp } from './admin/app';
import { OperatorApp } from './operator/app';

/**
 * Zwei Seiten, ein Aufbau:
 *   /w/<webinar>      Anmeldung
 *   /raum/<zugang>    Webinar-Raum (Wartezimmer, Video, Schluss-Seite)
 *   /aufzeichnung/<zugang>  Aufzeichnung nach dem Termin, solange sie gilt
 */
type Route =
  | { page: 'register'; slug: string }
  | { page: 'room'; token: string }
  | { page: 'cancel'; token: string }
  | { page: 'reschedule'; token: string }
  | { page: 'replay'; token: string }
  | { page: 'newsletter'; token: string }
  | { page: 'none' };

function routeFor(pathname: string): Route {
  const register = /^\/w\/([a-z0-9-]+)\/?$/.exec(pathname);
  if (register) return { page: 'register', slug: register[1]! };
  const room = /^\/raum\/([A-Za-z0-9_-]+)\/?$/.exec(pathname);
  if (room) return { page: 'room', token: room[1]! };
  const cancel = /^\/absage\/([A-Za-z0-9_-]+)\/?$/.exec(pathname);
  if (cancel) return { page: 'cancel', token: cancel[1]! };
  const reschedule = /^\/umbuchen\/([A-Za-z0-9_-]+)\/?$/.exec(pathname);
  if (reschedule) return { page: 'reschedule', token: reschedule[1]! };
  const replay = /^\/aufzeichnung\/([A-Za-z0-9_-]+)\/?$/.exec(pathname);
  if (replay) return { page: 'replay', token: replay[1]! };
  const newsletter = /^\/newsletter\/([A-Za-z0-9_-]+)\/?$/.exec(pathname);
  if (newsletter) return { page: 'newsletter', token: newsletter[1]! };
  return { page: 'none' };
}

/** Die Betreiber-Konsole wohnt unter admin.<Plattform> (lokal admin.localhost). */
function isOperatorHost(hostname: string): boolean {
  const labels = hostname.toLowerCase().split('.');
  return labels[0] === 'admin' && (labels.length === 3 || (labels.length === 2 && labels[1] === 'localhost'));
}

/** Die Plattform selbst (evercast.video, www, localhost) hat kein Projekt – dort steht der Auftritt der Plattform. */
function isPlatformHost(hostname: string): boolean {
  const host = hostname.toLowerCase();
  if (host === 'localhost' || host === '127.0.0.1') return true;
  const labels = host.split('.');
  return labels.length <= 2 || (labels.length === 3 && labels[0] === 'www');
}

export function App() {
  const [route] = useState(() => routeFor(window.location.pathname));
  const [project, setProject] = useState<Project | null | undefined>(undefined);
  const operator = isOperatorHost(window.location.hostname);
  const isAdmin = !operator && window.location.pathname.startsWith('/admin');
  const platform = !operator && isPlatformHost(window.location.hostname);

  useEffect(() => {
    if (operator || isAdmin || platform) return;
    fetchProject()
      .then((p) => {
        setProject(p);
        if (p) {
          applyTheme(p.theme);
          document.title = p.name;
        }
      })
      .catch(() => setProject(null));
  }, [operator, isAdmin, platform]);

  if (operator) return <OperatorApp />;
  if (isAdmin) return <AdminApp />;
  if (platform) {
    if (window.location.pathname === '/impressum') return <LegalPage kind="impressum" />;
    if (window.location.pathname === '/datenschutz') return <LegalPage kind="datenschutz" />;
    if (window.location.pathname === '/kostenlos-testen') return <TrialPage />;
    return <LandingPage />;
  }
  if (project === undefined) return null;
  if (project === null) {
    return (
      <main className="page page--message">
        <h1>Kein Projekt unter dieser Adresse</h1>
        <p className="muted">Bitte prüfe den Link, den du bekommen hast.</p>
      </main>
    );
  }

  switch (route.page) {
    case 'register':
      return <RegisterPage project={project} slug={route.slug} />;
    case 'room':
      return <RoomPage project={project} token={route.token} />;
    case 'cancel':
      return <CancelPage project={project} token={route.token} />;
    case 'reschedule':
      return <ReschedulePage project={project} token={route.token} />;
    case 'replay':
      return <ReplayPage project={project} token={route.token} />;
    case 'newsletter':
      return <NewsletterPage project={project} token={route.token} />;
    default:
      return (
        <main className="page page--message">
          <h1>{project.name}</h1>
          <p className="muted">Diese Seite gibt es nicht.</p>
        </main>
      );
  }
}
